import type { SessionState } from '../../data/session';

export type RouteView =
  | { kind: 'home' }
  | { kind: 'invite_needed' }
  | { kind: 'join_neighborhood'; token: string }
  | { kind: 'join_family'; token: string }
  | { kind: 'sign_in' }
  | { kind: 'invite_partner' }
  | { kind: 'headed_out_list' }
  | { kind: 'settings' };

/**
 * Decide where to navigate after a successful sign-in.
 * - When currently on a join/family path, stay put (return null).
 * - Otherwise prefer a pending invite path when present; fall back to '/'.
 */
export function postSignInTarget(currentPath: string, pending: string | null): string | null {
  if (currentPath.startsWith('/join/') || currentPath.startsWith('/family/')) {
    return null;
  }
  if (currentPath.startsWith('/sign-in')) {
    return pending ?? '/';
  }
  return pending ?? '/';
}

export function deriveRoute(pathname: string, session: SessionState): RouteView {
  const path = pathname || '/';
  // Normalize
  if (path.startsWith('/join/')) {
    const token = path.slice('/join/'.length);
    if (!session.isSignedIn) return { kind: 'sign_in' };
    return { kind: 'join_neighborhood', token };
  }
  if (path.startsWith('/family/')) {
    const token = path.slice('/family/'.length);
    if (!session.isSignedIn) return { kind: 'sign_in' };
    return { kind: 'join_family', token };
  }
  if (path.startsWith('/sign-in')) {
    if (!session.isSignedIn) return { kind: 'sign_in' };
    // Signed in: fall through to default handling
  }
  // Signed-out users see invite-needed for all other routes
  if (!session.isSignedIn) {
    return { kind: 'invite_needed' };
  }
  // Signed-in without family: block routes other than the join flow
  if (session.isSignedIn && !session.familyId) {
    return { kind: 'invite_needed' };
  }
  if (path.startsWith('/headed-out')) return { kind: 'headed_out_list' };
  if (path.startsWith('/invite-partner')) return { kind: 'invite_partner' };
  if (path.startsWith('/settings')) return { kind: 'settings' };
  // Signed-in: home vs invite-needed
  return { kind: 'home' };
}

